import { CAPTURE_WORKFLOW_ID } from "../actionItemHelpers";

export const RECIPIENT_PROFILE_STYLE_CONCISE = "concise";
export const RECIPIENT_PROFILE_STYLE_DETAILED = "detailed";

export const RECIPIENT_PROFILE_STYLE_OPTIONS = [
  {
    id: RECIPIENT_PROFILE_STYLE_CONCISE,
    label: "Concise",
    description: "Short, direct drafts",
  },
  {
    id: RECIPIENT_PROFILE_STYLE_DETAILED,
    label: "Detailed",
    description: "Fuller context in drafts",
  },
];

/** Hint under the Role & priorities field in settings and onboarding. */
export const RECIPIENT_PROFILE_FIELD_DESCRIPTION =
  "Describe your role, manager level (IC, manager, director, VP, etc.), and what the AI should prioritize on your action items.";

/**
 * Placeholder example for the Role & priorities textarea.
 *
 * @example
 * RECIPIENT_PROFILE_FIELD_PLACEHOLDER
 * // "e.g. AI engineer (IC) at Task Mantis, reports to VP Product — prioritize product work…"
 */
export const RECIPIENT_PROFILE_FIELD_PLACEHOLDER =
  "e.g. AI engineer (IC) at Task Mantis, reports to VP Product — prioritize product work and customer follow-ups over cold outreach.";

/** Intro copy shown above the profile field on first-time onboarding. */
export const RECIPIENT_PROFILE_INTRO_DESCRIPTION =
  "Add your role, manager level, and priorities so action items from connected platforms are summarized in your context.";

const ROLE_LINE_RE = /^Role:\s*(.+)$/im;
const STYLE_LINE_RE = /^Style:\s*(concise|detailed)\s*$/im;

/**
 * Split stored profile text into role + writing style.
 *
 * @example
 * parseRecipientProfileFields("Role: Founder\nStyle: concise");
 * // { role: "Founder", style: "concise" }
 *
 * @example
 * parseRecipientProfileFields("VP Sales focused on pipeline");
 * // { role: "VP Sales focused on pipeline", style: "concise" }
 */
export const parseRecipientProfileFields = (rawProfile) => {
  const text = String(rawProfile || "").trim();
  if (!text) {
    return { role: "", style: RECIPIENT_PROFILE_STYLE_CONCISE };
  }

  const roleMatch = text.match(ROLE_LINE_RE);
  const styleMatch = text.match(STYLE_LINE_RE);
  if (roleMatch || styleMatch) {
    const styleRaw = String(styleMatch?.[1] || RECIPIENT_PROFILE_STYLE_CONCISE).toLowerCase();
    return {
      role: roleMatch ? String(roleMatch[1] || "").trim() : "",
      style:
        styleRaw === RECIPIENT_PROFILE_STYLE_DETAILED
          ? RECIPIENT_PROFILE_STYLE_DETAILED
          : RECIPIENT_PROFILE_STYLE_CONCISE,
    };
  }

  return { role: text, style: RECIPIENT_PROFILE_STYLE_CONCISE };
};

/**
 * Build performer-config profile text from structured role + style fields.
 *
 * @example
 * composeRecipientProfileText({ role: "Founder", style: "concise" });
 * // "Founder. Write concise, direct drafts."
 */
export const composeRecipientProfileText = ({ role, style }) => {
  const trimmedRole = String(role || "").trim();
  if (!trimmedRole) return "";

  const normalizedStyle =
    style === RECIPIENT_PROFILE_STYLE_DETAILED
      ? RECIPIENT_PROFILE_STYLE_DETAILED
      : RECIPIENT_PROFILE_STYLE_CONCISE;
  const styleHint =
    normalizedStyle === RECIPIENT_PROFILE_STYLE_DETAILED
      ? "Write detailed drafts with fuller context."
      : "Write concise, direct drafts.";
  return `${trimmedRole}. ${styleHint}`;
};

/**
 * Build a lookup of workflow id → recipient profile text from list-workflows payloads.
 *
 * @example
 * buildRecipientProfileByWorkflowId([
 *   { workflow_id: "wf-1", recipient_profile: "I'm a founder…" },
 * ]);
 * // Map { "wf-1" => "I'm a founder…" }
 */
export const buildRecipientProfileByWorkflowId = (workflows) => {
  const map = new Map();
  (Array.isArray(workflows) ? workflows : []).forEach((workflow) => {
    const id = String(workflow?.workflow_id || "").trim();
    const profile = String(workflow?.recipient_profile || "").trim();
    if (id && profile) map.set(id, profile);
  });
  return map;
};

/**
 * Profile text for a workflow id, or empty when unknown.
 *
 * @example
 * resolveRecipientProfileForWorkflow("wf-1", new Map([["wf-1", "GTM lead"]]));
 * // "GTM lead"
 */
export const resolveRecipientProfileForWorkflow = (workflowId, profileByWorkflowId) => {
  const id = String(workflowId || "").trim();
  if (!id || id === CAPTURE_WORKFLOW_ID) return "";
  if (profileByWorkflowId instanceof Map) {
    return String(profileByWorkflowId.get(id) || "").trim();
  }
  if (profileByWorkflowId && typeof profileByWorkflowId === "object") {
    return String(profileByWorkflowId[id] || "").trim();
  }
  return "";
};

/**
 * Pick the non-empty recipient profile from the most recently updated workflow row.
 *
 * @example
 * findLatestRecipientProfile([
 *   { recipient_profile: "A", updated_at: 100 },
 *   { recipient_profile: "B", updated_at: 200 },
 * ]);
 * // "B"
 */
export const findLatestRecipientProfile = (workflows) => {
  const ranked = (Array.isArray(workflows) ? workflows : [])
    .map((workflow) => ({
      text: String(workflow?.recipient_profile || "").trim(),
      updated: Number(workflow?.updated_at) || 0,
    }))
    .filter((row) => row.text);
  if (ranked.length === 0) return "";
  ranked.sort((a, b) => b.updated - a.updated);
  return ranked[0].text;
};
